import React from "react";
import {
    FormSection, FormSectionHeader, FormSectionBody, FieldGrid, FormField, SaveBtn,
} from "./LabourWardStyles";

/**
 * LabourAdmissionForm — Labour ward admission form with structured sections
 */
export const LabourAdmissionForm = function(props) {
    const fd = props.formData;
    const hc = props.handleChange;
    const activeSection = props.activeSection;
    const saveAdmission = props.saveAdmission;
    const saving = props.saving;
    const k = props.constants;

    const renderSelect = function(field, options, placeholder) {
        return React.createElement("select", {
            value: fd[field] || "",
            onChange: function(e) { hc(field, e.target.value); },
        },
            React.createElement("option", { value: "" }, placeholder || "— Select —"),
            options.map(function(opt) {
                const val = typeof opt === "object" ? opt.value : opt;
                const lab = typeof opt === "object" ? opt.label : opt;
                return React.createElement("option", { key: val, value: val }, lab);
            })
        );
    };

    /* ═══ Section 1: Admission Details ═══ */
    const renderAdmissionDetails = function() {
        return React.createElement(FormSection, null,
            React.createElement(FormSectionHeader, null,
                React.createElement("h4", null, "🏥 Admission Details"),
                React.createElement("span", { className: "sh-badge" }, "Step 1")
            ),
            React.createElement(FormSectionBody, null,
                React.createElement(FieldGrid, { cols: 3 },
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Admission Date & Time"),
                        React.createElement("input", { type: "datetime-local",
                            value: fd.admission_datetime || "",
                            onChange: function(e) { hc("admission_datetime", e.target.value); } })
                    ),
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Referral Source"),
                        renderSelect("referral_source", k.REFERRAL_SOURCES, "— Select source —")
                    ),
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Gestational Age (weeks)"),
                        React.createElement("input", { type: "number", min: 20, max: 45,
                            placeholder: "e.g. 38",
                            value: fd.gestational_age_weeks || "",
                            onChange: function(e) { hc("gestational_age_weeks", e.target.value); } }),
                        React.createElement("div", { className: "hint" },
                            "Auto-filled from ANC booking if available")
                    )
                ),
                fd.referral_source && fd.referral_source !== "self"
                    ? React.createElement(FieldGrid, { cols: 1, style: { marginTop: 12 } },
                        React.createElement(FormField, null,
                            React.createElement("label", null, "Referral Reason"),
                            React.createElement("textarea", { rows: 2,
                                placeholder: "Reason for referral...",
                                value: fd.referral_reason || "",
                                onChange: function(e) { hc("referral_reason", e.target.value); } })
                        )
                    ) : null,
                React.createElement(FieldGrid, { cols: 3, style: { marginTop: 16, paddingTop: 16,
                    borderTop: "1px dashed #e0e0e0" } },
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Onset of Labour"),
                        renderSelect("onset_type", k.ONSET_TYPES, "— Select onset —")
                    ),
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Membrane Status"),
                        renderSelect("membrane_status", k.MEMBRANE_STATUS, "— Select —")
                    ),
                    fd.membrane_status === "ruptured"
                        ? React.createElement(FormField, null,
                            React.createElement("label", null, "Time of Membrane Rupture"),
                            React.createElement("input", { type: "datetime-local",
                                value: fd.membrane_rupture_time || "",
                                onChange: function(e) { hc("membrane_rupture_time", e.target.value); } }),
                            React.createElement("div", { className: "hint" },
                                "Important for infection risk assessment")
                        ) : null
                )
            )
        );
    };

    /* ═══ Section 2: Vital Signs & Initial Exam ═══ */
    const renderVitalSigns = function() {
        const bp_s = Number(fd.initial_bp_systolic);
        const bp_d = Number(fd.initial_bp_diastolic);
        const fhr = Number(fd.initial_fetal_hr);
        const temp = Number(fd.initial_temperature);

        return React.createElement(FormSection, null,
            React.createElement(FormSectionHeader, { bg: "#fff3e0", color: "#e65100" },
                React.createElement("h4", null, "🩺 Vital Signs & Initial Examination"),
                React.createElement("span", { className: "sh-badge" }, "Step 2")
            ),
            React.createElement(FormSectionBody, null,
                React.createElement("div", {
                    style: { fontSize: "0.78rem", color: "#78909c", marginBottom: 16, fontStyle: "italic" }
                }, "Record initial vital signs on admission to the labour ward"),

                React.createElement(FieldGrid, { cols: 4 },
                    React.createElement(FormField, null,
                        React.createElement("label", null, "BP Systolic (mmHg)"),
                        React.createElement("input", { type: "number", placeholder: "e.g. 120",
                            value: fd.initial_bp_systolic || "",
                            onChange: function(e) { hc("initial_bp_systolic", e.target.value); } }),
                        bp_s >= 140 ? React.createElement("div", { className: "warn" },
                            "⚠ Hypertension ≥140") : null
                    ),
                    React.createElement(FormField, null,
                        React.createElement("label", null, "BP Diastolic (mmHg)"),
                        React.createElement("input", { type: "number", placeholder: "e.g. 80",
                            value: fd.initial_bp_diastolic || "",
                            onChange: function(e) { hc("initial_bp_diastolic", e.target.value); } }),
                        bp_d >= 90 ? React.createElement("div", { className: "warn" },
                            "⚠ Hypertension ≥90") : null
                    ),
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Pulse Rate (bpm)"),
                        React.createElement("input", { type: "number", placeholder: "e.g. 78",
                            value: fd.initial_pulse || "",
                            onChange: function(e) { hc("initial_pulse", e.target.value); } })
                    ),
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Temperature (°C)"),
                        React.createElement("input", { type: "number", step: "0.1", placeholder: "e.g. 36.8",
                            value: fd.initial_temperature || "",
                            onChange: function(e) { hc("initial_temperature", e.target.value); } }),
                        temp >= 38 ? React.createElement("div", { className: "warn" },
                            "⚠ Fever ≥38°C") : null
                    )
                ),

                React.createElement(FieldGrid, { cols: 2, style: { marginTop: 16, paddingTop: 16,
                    borderTop: "1px dashed #e0e0e0" } },
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Cervical Dilation (cm)"),
                        React.createElement("input", { type: "number", min: 0, max: 10, step: 0.5,
                            placeholder: "0-10 cm",
                            value: fd.initial_cervical_dilation_cm || "",
                            onChange: function(e) { hc("initial_cervical_dilation_cm", e.target.value); } }),
                        React.createElement("div", { className: "hint" },
                            "Active labour typically ≥4cm")
                    ),
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Fetal Heart Rate (bpm)"),
                        React.createElement("input", { type: "number", placeholder: "110-160 normal",
                            value: fd.initial_fetal_hr || "",
                            onChange: function(e) { hc("initial_fetal_hr", e.target.value); } }),
                        fhr && (fhr < 110 || fhr > 160) ? React.createElement("div", { className: "warn" },
                            "⚠ Abnormal FHR (normal: 110-160)") : null
                    )
                )
            )
        );
    };

    /* ═══ Section 3: Obstetric Examination ═══ */
    const renderObstetricExam = function() {
        return React.createElement(FormSection, null,
            React.createElement(FormSectionHeader, { bg: "#e8f5e9", color: "#2e7d32" },
                React.createElement("h4", null, "🤰 Obstetric Examination"),
                React.createElement("span", { className: "sh-badge" }, "Step 3")
            ),
            React.createElement(FormSectionBody, null,
                React.createElement(FieldGrid, { cols: 3 },
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Fetal Presentation"),
                        renderSelect("fetal_presentation", k.FETAL_PRESENTATIONS, "— Select —")
                    ),
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Fetal Position"),
                        renderSelect("fetal_position", k.FETAL_POSITIONS, "— Select —")
                    ),
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Fetal Lie"),
                        renderSelect("fetal_lie", k.FETAL_LIES, "— Select —")
                    )
                ),
                React.createElement(FieldGrid, { cols: 2, style: { marginTop: 16 } },
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Pelvic Assessment"),
                        React.createElement("textarea", { rows: 2,
                            placeholder: "Clinical pelvic assessment findings...",
                            value: fd.pelvic_assessment || "",
                            onChange: function(e) { hc("pelvic_assessment", e.target.value); } })
                    ),
                    React.createElement(FormField, null,
                        React.createElement("label", null, "Management Plan"),
                        React.createElement("textarea", { rows: 2,
                            placeholder: "Planned management / delivery approach...",
                            value: fd.plan || "",
                            onChange: function(e) { hc("plan", e.target.value); } })
                    )
                ),

                /* Presentation warnings */
                fd.fetal_presentation && fd.fetal_presentation !== "cephalic"
                    ? React.createElement("div", {
                        style: {
                            marginTop: 12, padding: "10px 14px", background: "#fff3e0",
                            borderRadius: 8, border: "1px solid #ffcc80", fontSize: "0.82rem",
                            fontWeight: 600, color: "#e65100", display: "flex", alignItems: "center", gap: 8,
                        }
                    },
                        React.createElement("span", null, "⚠"),
                        "Non-cephalic presentation: " +
                            (fd.fetal_presentation === "breech" ? "Consider ECV or planned CS" :
                             "Transverse/oblique lie — CS likely required")
                    ) : null,

                fd.fetal_lie === "transverse"
                    ? React.createElement("div", {
                        style: {
                            marginTop: 8, padding: "10px 14px", background: "#ffebee",
                            borderRadius: 8, border: "1px solid #ef9a9a", fontSize: "0.82rem",
                            fontWeight: 600, color: "#b71c1c", display: "flex", alignItems: "center", gap: 8,
                        }
                    },
                        React.createElement("span", null, "🔴"),
                        "Transverse lie — Vaginal delivery not possible. Plan for Caesarean Section."
                    ) : null
            )
        );
    };

    /* ═══ Render active section ═══ */
    const sections = {
        admission: renderAdmissionDetails,
        vitals: renderVitalSigns,
        obstetric: renderObstetricExam,
    };

    const renderFn = sections[activeSection];

    return React.createElement("div", null,
        renderFn ? renderFn() : renderAdmissionDetails(),
        React.createElement("div", {
            style: { display: "flex", justifyContent: "flex-end", padding: "0 0 24px" }
        },
            React.createElement(SaveBtn, {
                disabled: saving,
                onClick: saveAdmission,
            }, saving ? "Saving..." : "🏥 Save Labour Ward Admission")
        )
    );
};
